import { useMemo, useState } from "react"

import { Markdown } from "@/components/chat/Markdown"
import { splitSections } from "@/components/help/sections"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useI18n, type MessageKey } from "@/i18n"
import type { HelpContent } from "@/types/api"

const EXAMPLE_QUESTIONS: MessageKey[] = ["help.example1", "help.example2", "help.example3"]

interface HelpPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  content: HelpContent | null
  /** Sends a message to the chat; used to put a question to the Help agent. */
  onAsk: (message: string) => void
}

export function HelpPanel({ open, onOpenChange, content, onAsk }: HelpPanelProps) {
  const { t } = useI18n()
  const [question, setQuestion] = useState("")
  const sections = useMemo(() => splitSections(content?.manual ?? ""), [content])
  const releases = useMemo(() => splitSections(content?.whats_new ?? ""), [content])

  const ask = (text: string) => {
    const clean = text.trim()
    if (!clean) return
    setQuestion("")
    onOpenChange(false)
    onAsk(`/help ${clean}`)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <div className="border-b border-border-subtle px-5 py-4">
          <SheetTitle>{t("help.title")}</SheetTitle>
          <SheetDescription>{t("help.description")}</SheetDescription>
        </div>
        {!content ? (
          <p className="p-5 text-sm text-fg3">{t("help.loadFailed")}</p>
        ) : (
          <Tabs defaultValue="guide" className="min-h-0 flex-1 gap-0">
            <TabsList className="mx-5 mt-3">
              <TabsTrigger value="guide">{t("help.tabGuide")}</TabsTrigger>
              <TabsTrigger value="new">{t("help.tabNew")}</TabsTrigger>
            </TabsList>
            <TabsContent value="guide" className="min-h-0 overflow-y-auto px-5 py-4">
              <nav aria-label={t("help.contentsLabel")} className="mb-4 flex flex-wrap gap-1.5">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    className="rounded-full border border-border px-2.5 py-1 text-xs text-fg2 hover:bg-hover"
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="mb-6">
                  <h3 className="mb-2 text-base font-semibold">{section.title}</h3>
                  <Markdown content={section.body} />
                </section>
              ))}
            </TabsContent>
            <TabsContent value="new" className="min-h-0 overflow-y-auto px-5 py-4">
              {releases.map((release) => (
                <section key={release.id} className="mb-5">
                  <h3 className="mb-1 text-sm font-semibold">{release.title}</h3>
                  <Markdown content={release.body} />
                </section>
              ))}
            </TabsContent>
          </Tabs>
        )}
        <form
          className="border-t border-border-subtle px-5 py-3"
          onSubmit={(event) => {
            event.preventDefault()
            ask(question)
          }}
        >
          <div className="mb-2 flex flex-wrap gap-1.5">
            {EXAMPLE_QUESTIONS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => ask(t(key))}
                className="rounded-full border border-border px-2.5 py-1 text-xs text-fg2 hover:bg-hover"
              >
                {t(key)}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder={t("help.askPlaceholder")}
              aria-label={t("help.askLabel")}
              maxLength={500}
              className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-sm outline-none focus:border-orange"
            />
            <Button type="submit" size="sm" disabled={!question.trim()}>
              {t("help.ask")}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
